// Shared YouTube search cache

export interface YouTubeVideo {
  id: string;
  title?: string;
  channel?: string;
}

interface YouTubeSearchResult {
  videos: YouTubeVideo[];
}

interface FetchOptions {
  artistName?: string;
  trackName?: string;
}

const YOUTUBE_SEARCH_URL = '/api/v1/youtube/search';

// Cache stores promises to avoid duplicate in-flight requests
const requestCache = new Map<string, Promise<YouTubeSearchResult>>();

export function fetchYouTubeVideos(query: string, options?: FetchOptions): Promise<YouTubeSearchResult> {
  const cached = requestCache.get(query);
  if (cached) {
    console.log(`[youtube] Cache hit for: ${query}`);
    return cached;
  }

  // Build URL with optional artist/track params for better filtering
  const params = new URLSearchParams({ q: query });
  if (options?.artistName) params.set('artist', options.artistName);
  if (options?.trackName) params.set('track', options.trackName);

  console.log(`[youtube] Fetching: ${query}`);
  const promise = fetch(`${YOUTUBE_SEARCH_URL}?${params}`)
    .then(response => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .catch(err => {
      // Remove failed requests from cache so they can be retried
      requestCache.delete(query);
      throw err;
    });

  requestCache.set(query, promise);
  return promise;
}

// Prefetch multiple queries in parallel (fire and forget)
export function prefetchYouTubeVideos(queries: string[]): void {
  queries.forEach(query => {
    if (!requestCache.has(query)) {
      fetchYouTubeVideos(query).catch(() => {
        // Ignore prefetch errors
      });
    }
  });
}

// Prime the cache with pre-resolved video IDs (from server's youtubeCache)
// This avoids making fresh requests for cached reports
export function primeYouTubeCache(youtubeCache: Record<string, string>): void {
  for (const [query, videoId] of Object.entries(youtubeCache)) {
    if (!requestCache.has(query)) {
      // Create a resolved promise with the pre-resolved video ID
      requestCache.set(query, Promise.resolve({ videos: [{ id: videoId }] }));
    }
  }
  console.log(`[youtube] Primed cache with ${Object.keys(youtubeCache).length} pre-resolved IDs`);
}
